import { IMAGE_FILTERS, ImageObjectFitTypes } from './constants';
import { supportWebGL, applyShader, glHelper } from '../lib/shader-filter';
import { filters } from '../lib/shaders';
import { drawRoundedRectStroke, renderRect } from './shape';
import { getClipImage } from './utils/image-clip';
import { resolveRadius } from './utils/radius';
import { getCacheByType } from './utils/raster';
import { resolveStrokeColor, resolveStrokeWidth } from './utils/stroke';
import { lowerFirst } from '../common-utils';
import { prefix } from '../lib/logger';
import { loadImageCache, getNodeImageCache } from '../provider/image';
import { renderWithCache } from './utils/render-cache';

const { warn } = prefix('image-render');

// Re-export loadImageCache for backward compatibility
export { loadImageCache };

export function renderImageRaw(renderContext, node, img) {
  const filter = node.filter;
  const filterItem = IMAGE_FILTERS.find((f) => f.key === filter);
  const intensity = node.filterIntensity ?? 50;
  if (!filterItem || !filters[lowerFirst(filterItem.key)]) return img;
  if (!intensity) return img;
  if (!supportWebGL()) {
    warn(`WebGL is not supported, cannot apply image filter`);
    return img;
  }

  // Create cache key for image filter
  const cacheItemKey = `${node.id}`;
  const cacheKey = `${filter}:${intensity}:${node.w}:${node.h}`;
  const caches = getCacheByType(renderContext, 'imageFilter');

  // Check if we have a cached result
  const existingCache = caches.get(cacheItemKey);
  if (existingCache && existingCache.key === cacheKey) {
    return existingCache;
  }

  if (existingCache) {
    existingCache.dispose?.call();
  }

  const dimensionLimit = Math.min(
    glHelper.maxTextureSize,
    node._filterPreview?.dimension ?? Number.MAX_SAFE_INTEGER
  );
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  const scale = Math.min(dimensionLimit / img.width, dimensionLimit / img.height, 1);

  canvas.width = img.img.width * scale;
  canvas.height = img.img.height * scale;
  context.drawImage(img.img, 0, 0, canvas.width, canvas.height);

  const cache = {
    key: cacheKey,
    dispose: () => {
      canvas.width = 0;
      canvas.height = 0;
    },
    img: canvas,
    width: canvas.width,
    height: canvas.height,
  };

  const shaderFilter = filters[lowerFirst(filterItem.key)];
  const param = shaderFilter.uniforms?.[0];

  applyShader(
    canvas,
    shaderFilter,
    param
      ? {
          [param.name]: param.normalize(intensity),
        }
      : {}
  );

  // Store result in cache
  caches.set(cacheItemKey, cache);

  return cache;
}

export function renderImage(renderContext, node) {
  const { context } = renderContext;
  const { radius } = node;

  loadImageCache(renderContext, node);

  const imgCache = getNodeImageCache(renderContext, node.id);
  const isLoading = node.loading || imgCache?.loading;
  const hasImage = imgCache?.img;

  if (!isLoading && hasImage) {
    renderImageWithContentCacheable(context, renderContext, node, radius);
  } else {
    renderImagePlaceholder(context, renderContext, node, imgCache);
  }
}

function renderImageWithContentCacheable(context, renderContext, node, radius) {
  // 图片使用设备 DPR（不强制 safeDPR=2）
  const getImageZoom = (ctx) => {
    const DPR = ctx.dpr || 1;
    const scale = ctx.providers?.scale?.scale || 1;
    return scale * DPR;
  };

  renderWithCache(context, renderContext, node, {
    cacheType: 'renderImage',

    getContentKey: (ctx, n) => ctx.providers?.dirty?.getContentKey(n.id),

    getTransformKey: (ctx, n) => ctx.providers?.dirty?.getTransformKey(n.id),

    getZoom: getImageZoom,

    shouldSkipCache: (ctx) => ctx.mode === 'export',

    renderToCache: (cacheContext, ctx, n) => {
      renderImageWithContent(cacheContext, ctx, n, radius);
    },

    renderDirect: (ctx, n) => renderImageWithContent(context, ctx, n, radius),
  });
}

export function renderImageWithContent(context, renderContext, node, radius) {
  const imgCache = getNodeImageCache(renderContext, node.id);
  if (!imgCache?.img) return;

  const _img = imgCache.img;
  const filteredImg = renderImageRaw(renderContext, node, {
    width: _img.width,
    height: _img.height,
    img: _img,
  });
  const clippedImg = getClipImage(renderContext, node, filteredImg);
  const { drawWidth, drawHeight, offsetX, offsetY } = calculateImageDimensions(node, clippedImg);
  renderImageContent(
    renderContext,
    context,
    node,
    radius,
    clippedImg,
    drawWidth,
    drawHeight,
    offsetX,
    offsetY
  );
  renderImageBorder(renderContext, context, node, radius, drawWidth, drawHeight, offsetX, offsetY);
}

function calculateImageDimensions(node, img) {
  const fit = node.fit || ImageObjectFitTypes.fill;
  const imgW = img.width;
  const imgH = img.height;
  const originRatio = imgW / imgH;
  let drawWidth, drawHeight, offsetX, offsetY;

  switch (fit) {
    case ImageObjectFitTypes.cover:
      ({ drawWidth, drawHeight, offsetX, offsetY } = calculateCoverDimensions(node, originRatio));
      break;
    case ImageObjectFitTypes.contain:
      ({ drawWidth, drawHeight, offsetX, offsetY } = calculateContainDimensions(node, originRatio));
      break;
    case ImageObjectFitTypes.scaleDown:
      ({ drawWidth, drawHeight, offsetX, offsetY } = calculateScaleDownDimensions(
        node,
        originRatio,
        img
      ));
      break;
    case ImageObjectFitTypes.top:
      ({ drawWidth, drawHeight, offsetX, offsetY } = calculateTopDimensions(node, originRatio));
      break;
    case ImageObjectFitTypes.fill:
    default:
      drawWidth = node.w;
      drawHeight = node.h;
      offsetX = 0;
      offsetY = 0;
      break;
  }

  return { drawWidth, drawHeight, offsetX, offsetY };
}

function calculateCoverDimensions(node, originRatio) {
  const targetRatio = node.w / node.h;
  if (originRatio > targetRatio) {
    return {
      drawHeight: node.h,
      drawWidth: node.h * originRatio,
      offsetX: (node.w - node.h * originRatio) / 2,
      offsetY: 0,
    };
  } else {
    return {
      drawWidth: node.w,
      drawHeight: node.w / originRatio,
      offsetY: (node.h - node.w / originRatio) / 2,
      offsetX: 0,
    };
  }
}

function calculateContainDimensions(node, originRatio) {
  const targetRatio = node.w / node.h;
  if (originRatio > targetRatio) {
    return {
      drawWidth: node.w,
      drawHeight: node.w / originRatio,
      offsetY: (node.h - node.w / originRatio) / 2,
      offsetX: 0,
    };
  } else {
    return {
      drawHeight: node.h,
      drawWidth: node.h * originRatio,
      offsetX: (node.w - node.h * originRatio) / 2,
      offsetY: 0,
    };
  }
}

function calculateScaleDownDimensions(node, originRatio, img) {
  const {
    drawWidth: containWidth,
    drawHeight: containHeight,
    offsetX,
    offsetY,
  } = calculateContainDimensions(node, originRatio);

  if (containWidth > img.width || containHeight > img.height) {
    return {
      drawWidth: img.width,
      drawHeight: img.height,
      offsetX: (node.w - img.width) / 2,
      offsetY: (node.h - img.height) / 2,
    };
  }

  return { drawWidth: containWidth, drawHeight: containHeight, offsetX, offsetY };
}

function calculateTopDimensions(node, originRatio) {
  return {
    drawWidth: node.w,
    drawHeight: node.w / originRatio,
    offsetY: 0,
    offsetX: 0,
  };
}

function renderImageBorder(
  renderContext,
  context,
  node,
  radius,
  drawWidth,
  drawHeight,
  offsetX,
  offsetY
) {
  const strokeWidths = resolveStrokeWidth(node.strokeWidth || 0);
  const strokeColors = resolveStrokeColor(node.stroke);
  if (!node.stroke || !strokeWidths.value) return;

  const fit = node.fit || ImageObjectFitTypes.fill;
  // Compute the base rectangle for the border depending on fit
  // - cover: use node rect
  // - others: use the drawn image rect (offset + draw size)
  const baseRect =
    fit === ImageObjectFitTypes.cover
      ? { x: node.x, y: node.y, w: node.w, h: node.h }
      : { x: node.x + offsetX, y: node.y + offsetY, w: drawWidth, h: drawHeight };

  // Adjust the rect similar to shape.renderRect: shift by half of side widths
  const adjustedRect = {
    x: baseRect.x + Math.max(strokeWidths.left, 0) / 2,
    y: baseRect.y + Math.max(strokeWidths.top, 0) / 2,
    w: Math.max(
      0,
      baseRect.w - (Math.max(strokeWidths.left, 0) + Math.max(strokeWidths.right, 0)) / 2
    ),
    h: Math.max(
      0,
      baseRect.h - (Math.max(strokeWidths.top, 0) + Math.max(strokeWidths.bottom, 0)) / 2
    ),
  };

  drawRoundedRectStroke(context, { ...node, ...adjustedRect }, strokeColors, strokeWidths);
}

function renderImageContent(
  renderContext,
  context,
  node,
  radius,
  img,
  drawWidth,
  drawHeight,
  offsetX,
  offsetY
) {
  const strokeWidths = resolveStrokeWidth(node.strokeWidth || 0);
  const fit = node.fit || ImageObjectFitTypes.fill;
  const innerCoords = calculateInnerCoordinates(
    node,
    fit,
    strokeWidths,
    drawWidth,
    drawHeight,
    offsetX,
    offsetY
  );

  const { topLeft, topRight, bottomRight, bottomLeft, isEmpty } = resolveRadius(
    innerCoords.width,
    innerCoords.height,
    radius
  );

  // 总是需要裁剪来避免图片溢出，但优化圆角处理
  context.save();
  context.beginPath();

  if (isEmpty || (!topLeft && !topRight && !bottomRight && !bottomLeft)) {
    // 无圆角时使用简单矩形裁剪，性能更优
    context.rect(innerCoords.x, innerCoords.y, innerCoords.width, innerCoords.height);
  } else {
    // 有圆角时才使用复杂路径
    drawRoundedPath(context, innerCoords, { topLeft, topRight, bottomRight, bottomLeft });
  }

  context.closePath();
  context.clip();

  if (renderContext.exportMode === 'ppt') {
    // Workaround: Initialize the clip region with a nearly transparent fill.
    // Some browsers fail to render drawImage() after clip() during export
    // if no prior draw operation has occurred within the clipped area.
    // This ensures the clip region is properly "activated" for subsequent drawing.
    context.fillStyle = 'rgba(0, 0, 0, 0.002)';
    context.fill();
  }

  try {
    if (node.background) {
      renderRect(
        { context },
        {
          x: node.x + offsetX,
          y: node.y + offsetY,
          w: drawWidth,
          h: drawHeight,
          radius: node.radius,
          fill: node.background,
        }
      );
    }
    context.drawImage(
      img.img,
      //  0, 0, img.width, img.height,
      node.x + offsetX,
      node.y + offsetY,
      drawWidth,
      drawHeight
    );
  } catch {
    // Silently handle image drawing errors
  }

  context.restore();
}

function calculateInnerCoordinates(
  node,
  fit,
  strokeWidths,
  drawWidth,
  drawHeight,
  offsetX,
  offsetY
) {
  const base =
    fit === ImageObjectFitTypes.cover
      ? { x: node.x, y: node.y, w: node.w, h: node.h }
      : { x: node.x + offsetX, y: node.y + offsetY, w: drawWidth, h: drawHeight };

  return {
    width: Math.max(
      0,
      base.w - (Math.max(strokeWidths.left, 0) + Math.max(strokeWidths.right, 0)) / 2
    ),
    height: Math.max(
      0,
      base.h - (Math.max(strokeWidths.top, 0) + Math.max(strokeWidths.bottom, 0)) / 2
    ),
    x: base.x + Math.max(strokeWidths.left, 0) / 2,
    y: base.y + Math.max(strokeWidths.top, 0) / 2,
  };
}

function drawRoundedPath(context, coords, radii) {
  context.moveTo(coords.x + radii.topLeft, coords.y);
  context.lineTo(coords.x + coords.width - radii.topRight, coords.y);
  context.arcTo(
    coords.x + coords.width,
    coords.y,
    coords.x + coords.width,
    coords.y + radii.topRight,
    radii.topRight
  );
  context.lineTo(coords.x + coords.width, coords.y + coords.height - radii.bottomRight);
  context.arcTo(
    coords.x + coords.width,
    coords.y + coords.height,
    coords.x + coords.width - radii.bottomRight,
    coords.y + coords.height,
    radii.bottomRight
  );
  context.lineTo(coords.x + radii.bottomLeft, coords.y + coords.height);
  context.arcTo(
    coords.x,
    coords.y + coords.height,
    coords.x,
    coords.y + coords.height - radii.bottomLeft,
    radii.bottomLeft
  );
  context.lineTo(coords.x, coords.y + radii.topLeft);
  context.arcTo(coords.x, coords.y, coords.x + radii.topLeft, coords.y, radii.topLeft);
}

function renderImagePlaceholder(context, renderContext, node, imgCache) {
  clip(context, node, () => {
    if (node.loading || (imgCache && imgCache.loading)) {
      renderLoading(context, renderContext, node);
    } else if (imgCache && imgCache.error) {
      renderError(context, renderContext, node);
    }
  });
}

const errorTextI18N = {
  zh: '图片暂时无法显示',
  ja: '画像を表示できません',
  en: 'Image unavailable',
};

function renderError(context, renderContext, node) {
  const errorSize = 24;
  const errorX = node.x + (node.w - errorSize) / 2;
  const errorY = node.y + (node.h - errorSize) / 2;

  context.fillStyle = '#e5e7eb';
  context.fillRect(node.x, node.y, node.w, node.h);

  const errorText = errorTextI18N[renderContext.locale] || errorTextI18N.en;

  context.font = '28px Arial';
  context.fillStyle = '#999';
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText(errorText, errorX + errorSize / 2, errorY + errorSize / 2);
}

function clip(context, node, render) {
  // Create a clipped loading indicator that respects the node's border radius
  context.save();
  const radius = node.radius;
  // Get border radius values for the node
  const { topLeft, topRight, bottomRight, bottomLeft } = resolveRadius(node.w, node.h, radius);

  // Create clipping path with rounded corners
  context.beginPath();
  context.moveTo(node.x + topLeft, node.y);
  context.lineTo(node.x + node.w - topRight, node.y);
  context.arcTo(node.x + node.w, node.y, node.x + node.w, node.y + topRight, topRight);
  context.lineTo(node.x + node.w, node.y + node.h - bottomRight);
  context.arcTo(
    node.x + node.w,
    node.y + node.h,
    node.x + node.w - bottomRight,
    node.y + node.h,
    bottomRight
  );
  context.lineTo(node.x + bottomLeft, node.y + node.h);
  context.arcTo(node.x, node.y + node.h, node.x, node.y + node.h - bottomLeft, bottomLeft);
  context.lineTo(node.x, node.y + topLeft);
  context.arcTo(node.x, node.y, node.x + topLeft, node.y, topLeft);
  context.closePath();
  context.clip();

  render();

  context.restore();
}

function renderLoading(context, renderContext, node) {
  // Get current time for animations
  const apiNow =
    context &&
    context.canvas &&
    renderContext &&
    renderContext.api &&
    typeof renderContext.api.now === 'function'
      ? () => renderContext.api.now()
      : () => (performance && performance.now ? performance.now() : Date.now());
  const now = apiNow();

  // Modern shimmer effect parameters
  const shimmerCycle = 1800; // Smooth 1.8s cycle
  const shimmerPhase = (now % shimmerCycle) / shimmerCycle;

  // Create sophisticated gradient background with shimmer
  const shimmerGradient = context.createLinearGradient(
    node.x - node.w * 0.5,
    node.y,
    node.x + node.w * 1.5,
    node.y + node.h
  );

  // Animated shimmer position
  const shimmerPos = (shimmerPhase * 1.5) % 1;
  const shimmerWidth = 0.3;

  shimmerGradient.addColorStop(Math.max(0, shimmerPos - shimmerWidth), 'rgba(240, 242, 247, 0.3)');
  shimmerGradient.addColorStop(shimmerPos, 'rgba(255, 255, 255, 0.7)');
  shimmerGradient.addColorStop(Math.min(1, shimmerPos + shimmerWidth), 'rgba(240, 242, 247, 0.3)');

  // Base skeleton background
  context.fillStyle = 'rgba(248, 250, 252, 0.8)';
  context.fillRect(node.x, node.y, node.w, node.h);

  // Apply shimmer overlay
  context.fillStyle = shimmerGradient;
  context.fillRect(node.x, node.y, node.w, node.h);

  // Calculate loading spinner size and position
  const loadingSize = Math.min(node.w, node.h) * 0.2;
  const loadingX = node.x + (node.w - loadingSize) / 2;
  const loadingY = node.y + (node.h - loadingSize) / 2;
  const centerX = loadingX + loadingSize / 2;
  const centerY = loadingY + loadingSize / 2;

  // Animate spinner
  const angle = ((now / 1000) % 1) * Math.PI * 2;
  const spinnerWidth = loadingSize * 0.1;

  // Draw animated arc with green gradient
  const arcGradient = context.createLinearGradient(
    centerX + (Math.cos(angle) * loadingSize) / 2,
    centerY + (Math.sin(angle) * loadingSize) / 2,
    centerX + (Math.cos(angle + Math.PI * 1.25) * loadingSize) / 2,
    centerY + (Math.sin(angle + Math.PI * 1.25) * loadingSize) / 2
  );
  arcGradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
  arcGradient.addColorStop(1, 'rgba(0, 0, 0, 0.9)');

  context.beginPath();
  context.arc(centerX, centerY, loadingSize / 2, angle, angle + Math.PI * 1.25);
  context.strokeStyle = arcGradient;
  context.lineWidth = spinnerWidth;
  context.lineCap = 'round';
  context.stroke();
}
